/**
 * The read layer. Every hook returns what the server sent.
 *
 * Nothing here compares, sorts, filters or merges: those are the server's answers to give, and
 * the ESLint rule of §6.4 fails a comparison anywhere under `src/api/`.
 */

import { useQuery, useQueries, keepPreviousData } from '@tanstack/react-query';
import { apiFetch, apiGet } from '../client';
import { keys } from '../keys';
import { routes } from '~/lib/routes';
import type * as T from '../schema';

type QueryValues = Record<string, string | number | boolean | undefined>;

const search = (params: QueryValues): string => {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    query.set(key, String(value));
  }
  const rendered = query.toString();
  return rendered ? `?${rendered}` : '';
};

/** `/meta` is everything a client needs before it can render anything, so it never goes stale. */
export function useMeta() {
  return useQuery({
    queryKey: keys.meta,
    queryFn: ({ signal }) => apiGet<T.Meta>(routes.api.meta(), signal),
    staleTime: Infinity,
  });
}

export function useAuthConfig() {
  return useQuery({
    queryKey: keys.authConfig,
    queryFn: ({ signal }) => apiGet<T.AuthConfig>(routes.api.authConfig(), signal),
    staleTime: Infinity,
    retry: false,
  });
}

export interface JurisdictionListParams {
  q?: string;
  sort?: string;
  dir?: string;
  page?: number;
  pageSize?: number;
  researchStatus?: string;
  incomplete?: string;
  asAt?: string;
  recordedAt?: string;
}

export function useJurisdictions(params: JurisdictionListParams) {
  return useQuery({
    queryKey: keys.jurisdictions(params as Record<string, string | number | undefined>),
    queryFn: ({ signal }) =>
      apiGet<T.JurisdictionList>(`${routes.api.jurisdictions()}${search({ ...params })}`, signal),
    placeholderData: keepPreviousData,
  });
}

export function useJurisdiction(code: string, axes: { asAt?: string; recordedAt?: string } = {}) {
  return useQuery({
    queryKey: keys.jurisdiction(code, axes),
    queryFn: async ({ signal }) => {
      const result = await apiFetch<T.JurisdictionDetail>(
        `${routes.api.jurisdiction(code)}${search(axes)}`,
        { signal },
      );
      return result.data;
    },
    enabled: code.length > 0,
  });
}

export function useSuccessions(code: string) {
  return useQuery({
    queryKey: keys.successions(code),
    queryFn: ({ signal }) => apiGet<T.Successions>(routes.api.successions(code), signal),
    enabled: code.length > 0,
  });
}

export interface MatterParams {
  filing?: string;
  asAt?: string;
  recordedAt?: string;
}

export function useMatterPanel(code: string, matter: string, params: MatterParams) {
  return useQuery({
    queryKey: keys.matter(code, matter, { ...params }),
    queryFn: ({ signal }) =>
      apiGet<T.MatterPanel>(`${routes.api.matter(code, matter)}${search({ ...params })}`, signal),
    enabled: code.length > 0 && matter.length > 0,
    placeholderData: keepPreviousData,
  });
}

export function useRuleHistory(
  code: string,
  matter: string,
  lane: string,
  recordedAt: string | undefined,
) {
  return useQuery({
    queryKey: keys.ruleHistory(code, matter, lane, recordedAt),
    queryFn: ({ signal }) =>
      apiGet<T.History>(
        `${routes.api.ruleHistory(code, matter, lane)}${search({ recordedAt })}`,
        signal,
      ),
    enabled: lane.length > 0,
  });
}

/**
 * Two reads of one rule's history: what it says now, and what it said as recorded at an instant.
 * Running them as one hook keeps the two columns of the history screen in step.
 */
export function useRuleHistoryPair(
  code: string,
  matter: string,
  lane: string,
  recordedAt: string | undefined,
) {
  return useQueries({
    queries: [
      {
        queryKey: keys.ruleHistory(code, matter, lane, undefined),
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          apiGet<T.History>(routes.api.ruleHistory(code, matter, lane), signal),
        enabled: lane.length > 0,
      },
      {
        queryKey: keys.ruleHistory(code, matter, lane, recordedAt),
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          apiGet<T.History>(
            `${routes.api.ruleHistory(code, matter, lane)}${search({ recordedAt })}`,
            signal,
          ),
        enabled: lane.length > 0 && recordedAt !== undefined,
      },
    ],
  });
}

/**
 * Changes to rules in force waiting for a second person (R44.4).
 *
 * Always empty when four-eyes is off, so the panel that renders it disappears rather than being
 * conditional on a setting the client would otherwise have to know about.
 */
export function usePendingChanges(code: string, matter: string) {
  return useQuery({
    queryKey: keys.pendingChanges(code, matter),
    queryFn: ({ signal }) =>
      apiGet<T.PendingChanges>(routes.api.pendingChanges(code, matter), signal),
    enabled: code.length > 0 && matter.length > 0,
  });
}

/** Everybody active, so an administrator can see who holds what. */
/**
 * Everyone a name can be offered for.
 *
 * Active only by default, which is the right answer to "who can I assign this to". The admin
 * screen asks for the suspended too: hiding them there would remove the only control that
 * could restore them.
 */
export function useUsers(options: { includeSuspended?: boolean } = {}) {
  const params = options.includeSuspended ? { includeSuspended: 'true' } : {};
  return useQuery({
    queryKey: keys.usersScoped(options.includeSuspended ?? false),
    queryFn: ({ signal }) => apiGet<T.Users>(`${routes.api.users()}${search(params)}`, signal),
  });
}

/** Every file that has left the building, with what it could not carry. */
export function useExports() {
  return useQuery({
    queryKey: keys.exports(),
    queryFn: ({ signal }) => apiGet<T.Exports>(routes.api.exports(), signal),
  });
}

export function useSources(code: string, matter: string) {
  return useQuery({
    queryKey: keys.sources(code, matter),
    queryFn: ({ signal }) => apiGet<T.Sources>(routes.api.sources(code, matter), signal),
    enabled: code.length > 0 && matter.length > 0,
  });
}

/**
 * What can and cannot be established about a register's rules.
 *
 * Two lists, not one: findings, and the checks that could not run. The second is not a shorter
 * first — a chain starting at the matter floor cannot have a hole, so reporting no holes would be
 * reporting the floor rather than the law.
 */
export function useGaps(code: string, matter: string) {
  return useQuery({
    queryKey: keys.gaps(code, matter),
    queryFn: ({ signal }) => apiGet<T.GapAnalysis>(routes.api.gaps(code, matter), signal),
    enabled: code.length > 0 && matter.length > 0,
  });
}

export function useRunImpact(runId: string) {
  return useQuery({
    queryKey: keys.runImpact(runId),
    queryFn: ({ signal }) => apiGet<T.RunImpact>(routes.api.runImpact(runId), signal),
    enabled: runId.length > 0,
  });
}

export function useCoverage(matter: string) {
  return useQuery({
    queryKey: keys.coverage(matter),
    queryFn: ({ signal }) => apiGet<T.Coverage>(routes.api.coverage(matter as never), signal),
    enabled: matter.length > 0,
  });
}

/** The queue polls while the tab is visible and stops when it is not (§8.2). */
export function useQueue(options: { poll?: boolean; matter?: string } = {}) {
  const params = options.matter ? { matter: options.matter } : {};
  return useQuery({
    queryKey: keys.queue(options.matter),
    queryFn: ({ signal }) =>
      apiGet<T.QueueResponse>(`${routes.api.queue()}${search(params)}`, signal),
    refetchInterval: options.poll === false ? false : 5000,
    refetchIntervalInBackground: false,
  });
}

export function useProposals(params: { matter?: string } = {}) {
  return useQuery({
    queryKey: keys.proposals(params.matter),
    queryFn: ({ signal }) =>
      apiGet<T.Proposals>(`${routes.api.proposals()}${search(params)}`, signal),
  });
}

export function useProposal(id: string) {
  return useQuery({
    queryKey: keys.proposal(id),
    queryFn: ({ signal }) => apiGet<T.ProposalDetail>(routes.api.proposal(id), signal),
    enabled: id.length > 0,
  });
}

export function usePrompts() {
  return useQuery({
    queryKey: keys.prompts,
    queryFn: ({ signal }) => apiGet<T.Prompts>(routes.api.prompts(), signal),
  });
}

export function usePromptVersions(id: string) {
  return useQuery({
    queryKey: keys.promptVersions(id),
    queryFn: ({ signal }) => apiGet<T.PromptVersions>(routes.api.promptVersions(id), signal),
    enabled: id.length > 0,
  });
}

export function useContradictions(params: { code?: string; matter?: string }) {
  return useQuery({
    queryKey: keys.contradictions(params),
    queryFn: ({ signal }) =>
      apiGet<T.Contradictions>(`${routes.api.contradictions()}${search(params)}`, signal),
  });
}

/**
 * What became of every row of an import.
 *
 * Totals were not the whole story: a sheet of 38 rows produced 2 rules and said nothing about
 * the other 36, because a partial success discarded the refusals it had collected.
 */
export function useImportRows(id: string | null) {
  return useQuery({
    queryKey: keys.importRows(id ?? ''),
    enabled: Boolean(id),
    queryFn: ({ signal }) =>
      apiGet<T.ImportRows>(routes.api.importRows(id ?? ''), signal),
  });
}

/** Every held rule, with why each one can or cannot reach Manage. */
export function useRuleCatalogue(params: Record<string, string | undefined>) {
  return useQuery({
    queryKey: keys.ruleCatalogue(params),
    queryFn: ({ signal }) => apiGet<T.RuleCatalogue>(`${routes.api.rules()}${search(params)}`, signal),
  });
}

/** What this engine cannot yet say to Manage. Nothing here maps anything; it makes the absence countable. */
export function useMappings() {
  return useQuery({
    queryKey: keys.mappings(),
    queryFn: ({ signal }) => apiGet<T.MappingReport>(routes.api.mappings(), signal),
  });
}

export function useRuns(params: {
  code?: string;
  matter?: string;
  state?: string;
  page?: number;
}) {
  return useQuery({
    queryKey: keys.runs(params),
    queryFn: ({ signal }) => apiGet<T.Runs>(`${routes.api.runs()}${search(params)}`, signal),
    placeholderData: keepPreviousData,
  });
}

export function useRun(id: string, options: { poll?: boolean } = {}) {
  return useQuery({
    queryKey: keys.run(id),
    queryFn: ({ signal }) => apiGet<T.RunDetail>(routes.api.run(id), signal),
    enabled: id.length > 0,
    refetchInterval: options.poll ? 5000 : false,
    refetchIntervalInBackground: false,
  });
}

export function useRunEvents(id: string, options: { poll?: boolean } = {}) {
  return useQuery({
    queryKey: keys.runEvents(id),
    queryFn: ({ signal }) => apiGet<T.RunEvents>(routes.api.runEvents(id), signal),
    enabled: id.length > 0,
    refetchInterval: options.poll ? 5000 : false,
    refetchIntervalInBackground: false,
  });
}
